import React, { useState, useMemo } from 'react';
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Music, Clock, MapPin, Users, Calendar as CalendarIcon, CheckCircle, Circle } from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CalendarView({ schedules = [], availabilityBlocks = [], members = [], onScheduleClick }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      const parsedDate = new Date(dateStr);
      return isValidDate(parsedDate) ? parsedDate : null;
    }
    const [, year, month, day] = match;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    return isValidDate(parsedDate) ? parsedDate : null;
  };

  const formatCalendarDate = (dateValue, pattern, fallback = 'Data indisponivel') => (
    isValidDate(dateValue) ? format(dateValue, pattern, { locale: ptBR }) : fallback
  );

  // Dias com escalas
  const scheduleDates = useMemo(() => {
    return schedules.map((s) => parseLocalDate(s.date)).filter(Boolean);
  }, [schedules]);

  // Dias com bloqueios
  const blockedDates = useMemo(() => {
    return availabilityBlocks.map((b) => parseLocalDate(b.blocked_date)).filter(Boolean);
  }, [availabilityBlocks]);

  // Escalas do dia selecionado
  const daySchedules = useMemo(() => {
    if (!isValidDate(selectedDate)) return [];
    return schedules.filter((schedule) => {
      const parsedDate = parseLocalDate(schedule.date);
      return isValidDate(parsedDate) && isSameDay(parsedDate, selectedDate);
    });
  }, [schedules, selectedDate]);

  // Bloqueios do dia selecionado
  const dayBlocks = useMemo(() => {
    if (!isValidDate(selectedDate)) return [];
    return availabilityBlocks.filter((block) => {
      const parsedDate = parseLocalDate(block.blocked_date);
      return isValidDate(parsedDate) && isSameDay(parsedDate, selectedDate);
    });
  }, [availabilityBlocks, selectedDate]);

  const modifiers = {
    scheduled: scheduleDates,
    blocked: blockedDates
  };

  const modifiersStyles = {
    scheduled: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      fontWeight: 'bold',
      position: 'relative'
    },
    blocked: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      textDecoration: 'line-through'
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Calendário de Escalas</h3>
          <div className="flex gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-100 rounded"></div>
              <span>Escalas</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 rounded"></div>
              <span>Indisponível</span>
            </div>
          </div>
        </div>
        
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => setSelectedDate(date || new Date())}
          locale={ptBR}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          className="rounded-md border"
        />
      </Card>

      {/* Escalas do dia selecionado */}
      {daySchedules.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">
            Escalas em {formatCalendarDate(selectedDate, "d 'de' MMMM")}
          </h3>
          <div className="space-y-2">
            {daySchedules.map(schedule => (
              <div
                key={schedule.id}
                onClick={() => setSelectedSchedule(schedule)}
                className="p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">{schedule.title}</h4>
                    <div className="space-y-1 mt-2 text-sm text-blue-700">
                      {schedule.start_time && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{schedule.start_time} {schedule.end_time && `- ${schedule.end_time}`}</span>
                        </div>
                      )}
                      {schedule.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{schedule.location}</span>
                        </div>
                      )}
                      {schedule.members?.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>
                            {schedule.members.filter(m => m.confirmed).length}/{schedule.members.length} confirmados
                          </span>
                        </div>
                      )}
                      {schedule.songs?.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Music className="w-4 h-4" />
                          <span>{schedule.songs.length} hino(s)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bloqueios do dia */}
      {dayBlocks.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">
            Indisponibilidades em {formatCalendarDate(selectedDate, "d 'de' MMMM")}
          </h3>
          <div className="space-y-2">
            {dayBlocks.map((block, idx) => (
              <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="font-semibold text-red-900">{block.worship_member_name}</p>
                {block.reason && (
                  <p className="text-sm text-red-700 mt-1">{block.reason}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {daySchedules.length === 0 && dayBlocks.length === 0 && (
        <Card className="p-8 text-center">
          <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">
            Nenhuma escala ou bloqueio em {formatCalendarDate(selectedDate, "d 'de' MMMM")}
          </p>
        </Card>
      )}

      {/* Dialog de detalhes da escala */}
      <Dialog open={!!selectedSchedule} onOpenChange={() => setSelectedSchedule(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Escala</DialogTitle>
          </DialogHeader>

          {selectedSchedule && (
            <div className="space-y-4 mt-4">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{selectedSchedule.title}</h3>
                <p className="text-sm text-gray-500">
                  {formatCalendarDate(parseLocalDate(selectedSchedule.date), "d 'de' MMMM 'de' yyyy")}
                </p>
              </div>

              {selectedSchedule.members?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Integrantes</p>
                  <div className="space-y-2">
                    {selectedSchedule.members.map((member, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          member.confirmed ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {member.confirmed ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-400" />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold">{member.name}</p>
                            {member.role && (
                              <Badge variant="secondary" className="mt-1">
                                Função: {member.role}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => onScheduleClick?.(selectedSchedule)}
                >
                  Ver Detalhes Completos
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedSchedule(null)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

